<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import LandingTrainingCard from './LandingTrainingCard.vue';
import LandingCtaButton from './LandingCtaButton.vue';
import { useScrollAnimations } from '@/composables/useScrollAnimations';
import trainingImg1 from '@/assets/training_img1.png';
import trainingImg2 from '@/assets/training_img2.png';
import trainingImg3 from '@/assets/training_img3.png';

const { t } = useI18n();

const trainingMethods = computed(() => [
    {
        id: 1,
        title: t('landingPage.v2.training.items[0].title'),
        image: trainingImg1,
        method: t('landingPage.v2.training.items[0].method'),
        expectations: t('landingPage.v2.training.items[0].expectations'),
        reality: t('landingPage.v2.training.items[0].reality'),
    },
    {
        id: 2,
        title: t('landingPage.v2.training.items[1].title'),
        image: trainingImg2,
        method: t('landingPage.v2.training.items[1].method'),
        expectations: t('landingPage.v2.training.items[1].expectations'),
        reality: t('landingPage.v2.training.items[1].reality'),
    },
    {
        id: 3,
        title: t('landingPage.v2.training.items[2].title'),
        image: trainingImg3,
        method: t('landingPage.v2.training.items[2].method'),
        expectations: t('landingPage.v2.training.items[2].expectations'),
        reality: t('landingPage.v2.training.items[2].reality'),
    },
]);

const { animateInView, slideUp } = useScrollAnimations();
const sectionRef = ref(null);
const cardsRef = ref([]);

onMounted(() => {
    if (sectionRef.value) {
        animateInView(sectionRef.value, () => {
            cardsRef.value.forEach((card, index) => {
                slideUp(card, index * 0.2);
            });
        }, 0.1);
    }
});
</script>

<template>
    <div class="py-24" ref="sectionRef">
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <!-- Header -->
            <div class="max-w-3xl mb-16">
                <h2 class="text-3xl font-bold tracking-tight text-white sm:text-5xl mb-6"
                    v-html="$t('landingPage.v2.training.header.title')">
                </h2>
                <div class="space-y-4 text-lg text-gray-400 leading-relaxed">
                    <p>{{ $t('landingPage.v2.training.header.subtitle1') }}</p>
                    <p>{{ $t('landingPage.v2.training.header.subtitle2') }}</p>
                </div>
            </div>

            <!-- Grid -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                <div v-for="(item, index) in trainingMethods" :key="item.id" ref="cardsRef" class="opacity-0">
                    <LandingTrainingCard :title="item.title" :method="item.method" :expectations="item.expectations"
                        :reality="item.reality" :image="item.image">
                        <template #icon>
                            <!-- Icons based on title -->
                            <svg v-if="index === 0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                            </svg>
                            <svg v-else-if="index === 1" xmlns="http://www.w3.org/2000/svg" fill="none"
                                viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
                            </svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </template>
                    </LandingTrainingCard>
                </div>
            </div>

            <!-- CTA -->
            <div class="flex justify-center">
                <LandingCtaButton sectionName="Training" :text="$t('landingPage.v2.cta.training')" />
            </div>
        </div>

    </div>
</template>
